/**
 * ChannelHeatmapView -- the channel resilience cross-tab (P8.3).
 *
 * Rows = IBSes, columns = channels. Each cell is empty (no link)
 * or rendered with the IBS's resilience pillar tone applied to the
 * row. AsOfPicker in the toolbar drives `?as_of=` -> rewinds the
 * pillar overlay.
 *
 * Routes:
 *   /twin/channels
 *
 * Backend: GET /v1/heatmap/channels?as_of= (twin-licensed).
 */
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { AsOfPicker } from "shared/components/AsOfPicker";
import { useAsOf } from "shared/hooks/useAsOf";
import { useChannelHeatmap } from "twin/api/hooks";
import type { HeatmapCell, LandscapePillar } from "shared/api/types";

import { IBSChannelStateDrawer } from "./IBSChannelStateDrawer";

// v1's tone palette ported verbatim so customers crossing v1 demos
// see the same colour-coding.
const PILLAR_TONE: Record<LandscapePillar | "empty" | "not_assessed", string> = {
  red: "bg-rose-200 hover:bg-rose-300",
  amber: "bg-amber-200 hover:bg-amber-300",
  green: "bg-emerald-200 hover:bg-emerald-300",
  unknown: "bg-slate-100 hover:bg-slate-200",
  // Round 13 B1 -- deliberately NOT a shade of green. This screen tinted
  // every row green on a bank that had assessed nothing, under copy
  // calling itself the signal a regulator looks for.
  not_assessed: "bg-slate-50 hover:bg-slate-100",
  empty: "bg-white",
};

const PILLAR_PILL: Record<LandscapePillar | "no-data" | "not_assessed", string> = {
  red: "bg-rose-100 text-rose-900",
  amber: "bg-amber-100 text-amber-900",
  green: "bg-emerald-100 text-emerald-900",
  unknown: "bg-slate-100 text-slate-600",
  not_assessed: "bg-slate-50 text-slate-500",
  "no-data": "bg-slate-50 text-slate-500",
};

export function ChannelHeatmapView() {
  const { t } = useTranslation();
  const as_of = useAsOf() ?? undefined;
  const query = useChannelHeatmap({ as_of });

  // P18 (#500): index cells by composite key for O(1) lookup;
  // value is the full HeatmapCell (carries state + state_source).
  const cellByKey = useMemo(() => {
    if (!query.data) return new Map<string, HeatmapCell>();
    return new Map(query.data.cells.map((c) => [`${c.ibs_id}|${c.channel_id}`, c]));
  }, [query.data]);

  // P18 (#500): drawer state for the selected cell (admin set-state UI).
  const [selectedCell, setSelectedCell] = useState<{
    ibsId: string;
    channelId: string;
  } | null>(null);

  return (
    <div data-testid="channel-heatmap-view" className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {t("screens.channelHeatmapView.channelResilienceHeatmap")}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t(
              "screens.channelHeatmapView.whichIbsesServeCustomersThroughWhichChannels",
            )}
          </p>
        </div>
        <AsOfPicker />
      </header>

      {query.isLoading && (
        <div className="rounded-md bg-slate-50 p-6 text-center text-slate-600">
          {t("screens.channelHeatmapView.loadingHeatmap")}
        </div>
      )}
      {query.error && (
        <div
          data-testid="heatmap-error"
          className="rounded-md border border-rose-200 bg-rose-50 p-4 text-rose-900"
        >
          {t("screens.channelHeatmapView.failedToLoadHeatmap")} {query.error.message}
        </div>
      )}

      {query.data && (
        <div className="overflow-x-auto">
          <table
            data-testid="heatmap-grid"
            className="min-w-full border-collapse text-sm"
          >
            <thead>
              <tr className="border-b border-slate-200">
                <th
                  scope="col"
                  className="sticky start-0 z-10 bg-white px-3 py-2 text-start text-xs font-semibold uppercase tracking-wide text-slate-600"
                >
                  {t("screens.channelHeatmapView.ibs")}
                </th>
                {query.data.channels.map((ch) => (
                  <th
                    key={ch.id}
                    scope="col"
                    data-testid="heatmap-channel-header"
                    data-channel-code={ch.code}
                    className="px-2 py-2 text-xs font-medium text-slate-800"
                    style={{
                      backgroundColor: ch.color_hex ? `${ch.color_hex}40` : undefined,
                    }}
                  >
                    {ch.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {query.data.ibses.map((ibs) => {
                const tone = ibs.resilience_pillar
                  ? PILLAR_TONE[ibs.resilience_pillar]
                  : PILLAR_TONE.unknown;
                return (
                  <tr
                    key={ibs.id}
                    data-testid="heatmap-ibs-row"
                    data-ibs-id={ibs.id}
                    data-pillar={ibs.resilience_pillar ?? ""}
                    data-mvb={ibs.mvb_flag ? "true" : "false"}
                    className="border-b border-slate-100"
                  >
                    <th
                      scope="row"
                      className="sticky start-0 z-10 bg-white px-3 py-1.5 text-start font-medium text-slate-900"
                    >
                      <Link to={`/twin/ibs/${ibs.id}`} className="hover:underline">
                        {ibs.name}
                      </Link>
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <span
                          data-testid="heatmap-pillar-pill"
                          className={`rounded px-1.5 py-0.5 ${PILLAR_PILL[ibs.resilience_pillar ?? "no-data"]}`}
                        >
                          {/* ROUND 15 B8 -- the raw token ("not_assessed")
                              was printed to a reader. A word. */}
                          {ibs.resilience_pillar
                            ? t(
                                `screens.channelHeatmapView.pillar.${ibs.resilience_pillar}`,
                                {
                                  defaultValue: ibs.resilience_pillar,
                                },
                              )
                            : t("screens.channelHeatmapView.noData")}
                        </span>
                        {ibs.mvb_flag && (
                          <span className="rounded bg-violet-200 px-1.5 py-0.5 text-violet-900">
                            {t("screens.channelHeatmapView.mvb")}
                          </span>
                        )}
                      </div>
                    </th>
                    {query.data.channels.map((ch) => {
                      const cell = cellByKey.get(`${ibs.id}|${ch.id}`);
                      const present = cell !== undefined;
                      // P18 (#500): per-cell state wins; otherwise
                      // fall back to row pillar. state_source on the
                      // cell payload reports which path was taken.
                      const cellTone = cell?.state
                        ? PILLAR_TONE[cell.state]
                        : present
                          ? tone
                          : PILLAR_TONE.empty;
                      const isOverride = cell?.state_source === "channel";
                      return (
                        <td
                          key={ch.id}
                          data-testid="heatmap-cell"
                          data-present={present ? "true" : "false"}
                          data-state={cell?.state ?? ""}
                          data-state-source={cell?.state_source ?? ""}
                          onClick={
                            present
                              ? () =>
                                  setSelectedCell({
                                    ibsId: ibs.id,
                                    channelId: ch.id,
                                  })
                              : undefined
                          }
                          className={`border-s border-slate-100 px-2 py-1.5 text-center ${cellTone} ${
                            present ? "cursor-pointer" : ""
                          }`}
                        >
                          {present ? (
                            <span className="inline-flex items-center gap-1">
                              <span>●</span>
                              {isOverride && (
                                <span
                                  data-testid="heatmap-cell-override-dot"
                                  className="inline-block h-1.5 w-1.5 rounded-full bg-slate-900"
                                  title={t(
                                    "screens.channelHeatmapView.perChannelOverride",
                                  )}
                                />
                              )}
                            </span>
                          ) : (
                            ""
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {query.data.ibses.length === 0 && (
            <div
              data-testid="heatmap-empty-state"
              className="mt-4 rounded border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600"
            >
              {/* B8 -- fourth round: it named an API path. A sentence,
                  and the register it means. */}
              {t("screens.channelHeatmapView.noIbsesYet")}{" "}
              <Link
                to="/foundation/admin/ibses"
                className="text-brand-brassDark underline decoration-dotted underline-offset-2"
              >
                {t("screens.channelHeatmapView.noIbsesYetLink")}
              </Link>
            </div>
          )}
        </div>
      )}

      {selectedCell &&
        query.data &&
        (() => {
          const ibs = query.data.ibses.find((i) => i.id === selectedCell.ibsId);
          const channel = query.data.channels.find(
            (c) => c.id === selectedCell.channelId,
          );
          const cell = cellByKey.get(`${selectedCell.ibsId}|${selectedCell.channelId}`);
          if (!ibs || !channel || !cell) return null;
          return (
            <IBSChannelStateDrawer
              ibs={ibs}
              channel={channel}
              cell={cell}
              onClose={() => setSelectedCell(null)}
            />
          );
        })()}
    </div>
  );
}
